import { useLocalSearchParams, useRouter } from 'expo-router';
import { useMemo } from 'react';
import { useTranslation } from 'react-i18next';
import { Pressable, ScrollView, StyleSheet, Text, View } from 'react-native';
import { useSafeAreaInsets } from 'react-native-safe-area-context';

import { fontSize, spacing } from '@/constants/theme';
import type { AppColors } from '@/constants/theme';
import { useTheme } from '@/contexts/ThemeContext';
import { PRIVACY, TERMS } from '@/lib/legalContent';

export default function LegalScreen() {
  const { t } = useTranslation();
  const { colors } = useTheme();
  const styles = useMemo(() => makeStyles(colors), [colors]);
  const { doc } = useLocalSearchParams<{ doc?: string }>();
  const router = useRouter();
  const insets = useSafeAreaInsets();

  const data = doc === 'privacy' ? PRIVACY : TERMS;

  return (
    <ScrollView
      style={styles.flex}
      contentContainerStyle={[
        styles.content,
        { paddingTop: insets.top + spacing.lg, paddingBottom: insets.bottom + spacing.xl },
      ]}
    >
      <View style={styles.topBar}>
        <Pressable onPress={() => router.back()} hitSlop={12} accessibilityRole="button">
          <Text style={styles.back}>{t('legalScreen.back')}</Text>
        </Pressable>
      </View>

      <Text style={styles.title}>{data.title}</Text>
      <Text style={styles.updated}>{data.updated}</Text>

      {data.sections.map((s) => (
        <View key={s.heading} style={styles.section}>
          <Text style={styles.heading}>{s.heading}</Text>
          <Text style={styles.body}>{s.body}</Text>
        </View>
      ))}
    </ScrollView>
  );
}

function makeStyles(colors: AppColors) {
  return StyleSheet.create({
    flex: { flex: 1, backgroundColor: colors.background },
    content: { paddingHorizontal: spacing.lg, gap: spacing.md },
    topBar: { flexDirection: 'row' },
    back: { color: colors.primaryLight, fontSize: fontSize.md, fontWeight: '600' },
    title: { color: colors.text, fontSize: fontSize.xl, fontWeight: '700' },
    updated: { color: colors.textMuted, fontSize: fontSize.sm, marginBottom: spacing.sm },
    section: { gap: spacing.xs },
    heading: { color: colors.text, fontSize: fontSize.md, fontWeight: '700' },
    body: { color: colors.textMuted, fontSize: fontSize.md, lineHeight: 22 },
  });
}
